import { IBoundingBox, IRay, IVector2, IVector3 } from '../api.js';
import { RENDER_LAYER_IGNORE_RAYCAST, RENDER_LAYER_MASK_RAYCASTABLE, RenderLayerMask } from './RenderLayer.js';
import { Ray as BABYLON_Ray } from '@inter-ikea-betrakta-babylonjs/core/Culling/ray.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { InstancedMesh as BABYLON_InstancedMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/instancedMesh.js';
import { lerp, matrix_pool, vector2_pool, vector3_pool } from '../Math.js';
import { is_aabb, is_sphere } from './utility/Bounds.js';
import { VertexBuffer as BABYLON_VertexBuffer } from '@inter-ikea-betrakta-babylonjs/core/Meshes/buffer.js';
import { assert } from './utility/Assert.js';
/**
 * Raycast the scene using a ray, return the closest SceneObject if that object has any drag or click handler, otherwise return null
 * @param ray The ray to use for picking
 * @param layer_mask The layer mask to select which layers to include in the raycast
 * @returns The SceneObject behind the given ray if it is pickable, null otherwise
 */
export function raycast_interactable_object(viewer, ray, layer_mask) {
    const hits = raycast_all_internal(viewer, ray, layer_mask);
    const closest_hit = filter_closest_hit(hits);
    if (closest_hit) {
        const { object } = closest_hit;
        const is_gizmo = typeof object === 'object';
        // TODO casting should be removed once Scene Objects will get an API
        const interactable = is_gizmo ? object : viewer.viewer_scene.objects.get(object);
        if (viewer.get_input_handler_manager().interactable_is_pickable(interactable)) {
            return [interactable, closest_hit];
        }
    }
    return null;
}
/**
 * Raycast all SceneObjects meshes and extra bounds
 * @param viewer viewer reference
 * @param ray Ray to use
 * @param hits Optional Storage array to append hits to
 * @param filter_layer_mask Optional filter for which layers to raycast against
 * @returns Array with all hits appended, if @hits was not provided a new array with all hits
 */
export function raycast_all_internal(viewer, ray, filter_layer_mask = RENDER_LAYER_MASK_RAYCASTABLE) {
    const gizmo_hits = raycast_all_gizmos_internal(viewer, ray);
    const object_mesh_hits = raycast_all_scene_meshes_internal(viewer, ray, filter_layer_mask);
    const object_bound_hits = raycast_all_bounds_internal(viewer, ray, filter_layer_mask);
    const hits = [].concat(gizmo_hits, object_mesh_hits, object_bound_hits);
    return hits;
}
export function raycast_all_objects_internal(viewer, ray, filter_layer_mask = RENDER_LAYER_MASK_RAYCASTABLE) {
    const object_mesh_hits = raycast_all_scene_meshes_internal(viewer, ray, filter_layer_mask);
    const object_bound_hits = raycast_all_bounds_internal(viewer, ray, filter_layer_mask);
    return object_mesh_hits.concat(object_bound_hits);
}
/**
 * Raycasts all gizmo meshes in the scene
 * @param ray The ray to use
 * @param hits optional result storage, pushes to the provided array if provided
 * @returns Array with one entry per mesh that was hit
 */
export function raycast_all_gizmos_internal(viewer, ray) {
    const hits = [];
    for (const instance of viewer.asset_manager.gizmos) {
        for (const mesh of instance.instances) {
            if (!mesh.isVisible)
                continue;
            const pick_info = ray.intersectsMesh(mesh, false, filter_backface_triangles);
            if (pick_info.hit && pick_info.pickedMesh && pick_info.pickedPoint) {
                hits.push({
                    world_position: pick_info.pickedPoint,
                    object: instance,
                    distance: pick_info.distance
                });
            }
        }
    }
    return hits;
}
/**
 * Raycasts all meshes in the scene while skipping those on the ignore_raycast render layer
 * Note that this function can return several hits per scene object
 * @param ray The ray to use
 * @param hits optional result storage, pushes to the provided array if provided
 * @returns Array with one entry per mesh that was hit
 */
export function raycast_all_scene_meshes_internal(viewer, ray, filter_layer_mask = RENDER_LAYER_MASK_RAYCASTABLE) {
    var _a, _b;
    const meshes = [];
    const hits = [];
    for (const scene_object of viewer.viewer_scene.objects.values()) {
        const layer_mask = scene_object.get_render_layer_mask();
        if (scene_object.mesh_nodes &&
            scene_object.enabled &&
            scene_object.visible &&
            (layer_mask.bitmap & filter_layer_mask.bitmap) !== 0 &&
            !RenderLayerMask.has_layer(layer_mask, RENDER_LAYER_IGNORE_RAYCAST)) {
            for (let i = 0; i < scene_object.mesh_nodes.length; i++) {
                meshes.push(scene_object.mesh_nodes[i]);
            }
        }
    }
    const local_ray = new BABYLON_Ray(BABYLON_Vector3.Zero(), BABYLON_Vector3.Forward(true), Number.MAX_VALUE);
    for (let i = 0; i < meshes.length; i++) {
        const mesh = meshes[i];
        const positions = mesh.getVerticesData(BABYLON_VertexBuffer.PositionKind);
        const indices = mesh.getIndices();
        const world_matrix = mesh.getWorldMatrix();
        const world_matrix_inverse = world_matrix.invertToRef(matrix_pool.fetch());
        BABYLON_Ray.TransformToRef(ray, world_matrix_inverse, local_ray);
        matrix_pool.return(world_matrix_inverse);
        const hit = {
            distance: Number.POSITIVE_INFINITY,
            world_position: new IVector3(NaN, NaN, NaN),
            object: -1
        };
        const metadata = viewer.viewer_scene.get_metadata_from_babylon_mesh(mesh);
        if (metadata === null)
            continue;
        if (raycast_bvh(local_ray, metadata.raycast_acceleration_structure, positions, indices, hit, (_b = (_a = mesh.material) === null || _a === void 0 ? void 0 : _a.backFaceCulling) !== null && _b !== void 0 ? _b : true, false)) {
            const hit_object = metadata.asset_instance_metadata.owner;
            assert(hit_object !== null, 'Error: The mesh hit by raycast did not correspond to a viewer scene object');
            const local_hit_position = new BABYLON_Vector3().fromIVector3(hit.world_position);
            const world_hit_position = vector3_pool.fetch();
            BABYLON_Vector3.TransformCoordinatesToRef(local_hit_position, world_matrix, world_hit_position);
            IVector3.copy_to_ref(world_hit_position, hit.world_position);
            vector3_pool.return(world_hit_position);
            hit.object = hit_object.id;
            hit.distance = IVector3.distance(ray.origin, hit.world_position);
            hits.push(hit);
        }
    }
    return hits;
}
export function raycast_all_bounds_internal(viewer, ray, filter_layer_mask = RENDER_LAYER_MASK_RAYCASTABLE) {
    const local_ray = new BABYLON_Ray(BABYLON_Vector3.Zero(), BABYLON_Vector3.Forward(true), Number.MAX_VALUE);
    const hits = [];
    viewer.viewer_scene.objects.forEach((scene_object) => {
        if (!scene_object.enabled ||
            !scene_object.visible ||
            RenderLayerMask.has_layer(scene_object.get_render_layer_mask(), RENDER_LAYER_IGNORE_RAYCAST) ||
            (scene_object.get_render_layer_mask().bitmap & filter_layer_mask.bitmap) === 0)
            return;
        const raycast_bounds = scene_object.get_raycast_bounds();
        if (raycast_bounds && raycast_bounds.length > 0) {
            // Transform ray to object local space
            const world_matrix = scene_object.get_world_matrix();
            const world_matrix_inverse = world_matrix.invertToRef(matrix_pool.fetch());
            BABYLON_Ray.TransformToRef(ray, world_matrix_inverse, local_ray);
            const hit = { world_position: { x: 0, y: 0, z: 0 }, object: -1, distance: Number.MAX_VALUE };
            const local_hit_position = vector3_pool.fetch();
            const world_hit_position = vector3_pool.fetch();
            const hit_cache = vector2_pool.fetch();
            for (const bound of raycast_bounds) {
                // Do the raycast in object local space
                let local_distance = -1;
                let world_distance = -1;
                // Find distance in local space
                if (is_aabb(bound)) {
                    if (IRay.intersects_bounding_box(local_ray, bound, hit_cache)) {
                        local_distance = hit_cache.x >= 0 ? hit_cache.x : hit_cache.y;
                    }
                }
                else if (is_sphere(bound)) {
                    if (IRay.intersects_sphere(local_ray, bound, hit_cache)) {
                        local_distance = hit_cache.x >= 0 ? hit_cache.x : hit_cache.y;
                    }
                }
                // If we have a hit in local space, transform it back to world
                if (local_distance >= 0) {
                    local_hit_position.copyFrom(local_ray.origin);
                    local_ray.direction.scaleAndAddToRef(local_distance, local_hit_position);
                    BABYLON_Vector3.TransformCoordinatesToRef(local_hit_position, world_matrix, world_hit_position);
                    world_distance = BABYLON_Vector3.Distance(world_hit_position, ray.origin);
                }
                // If the new hit is closer than the old one we store that hit instead
                if (world_distance >= 0 && world_distance <= ray.length && world_distance < hit.distance) {
                    hit.world_position.x = world_hit_position.x;
                    hit.world_position.y = world_hit_position.y;
                    hit.world_position.z = world_hit_position.z;
                    hit.object = scene_object.id;
                    hit.distance = world_distance;
                }
            }
            matrix_pool.return(world_matrix_inverse);
            vector3_pool.return(local_hit_position, world_hit_position);
            vector2_pool.return(hit_cache);
            if (hit.object !== -1) {
                hits.push(hit);
            }
        }
    });
    return hits;
}
/**
 * Given a PickingInfo it returns null if no object was hit or a ObjectRaycastResult
 * @param pick_info babylon pick result
 * @returns ObjectRaycastResult if viewer object was hit, null otherwise
 */
export function convert_pick_info(viewer, pick_info) {
    if (pick_info.hit && pick_info.pickedMesh) {
        assert(pick_info.pickedMesh instanceof BABYLON_InstancedMesh, 'Error: raycast mesh is not an instanced mesh, this should never happen');
        const mesh_metadata = viewer.viewer_scene.get_metadata_from_babylon_mesh(pick_info.pickedMesh);
        if (mesh_metadata !== null && pick_info.pickedPoint !== null) {
            const object = mesh_metadata.asset_instance_metadata.owner;
            if (object !== null) {
                return {
                    world_position: IVector3.copy_to_ref(pick_info.pickedPoint, new IVector3()),
                    object: object.id,
                    distance: pick_info.distance
                };
            }
        }
    }
    return null;
}
export function filter_closest_hit(hits) {
    // If hit result contains draw-on-top gizmos - only use those to pick closest
    const draw_on_top_gizoms = hits.filter(({ object }) => typeof object !== 'number' && object.mesh_source.draw_on_top);
    if (draw_on_top_gizoms.length) {
        hits = draw_on_top_gizoms;
    }
    if (hits.length === 0)
        return null;
    let closest_hit = hits[0];
    for (let i = 1; i < hits.length; i++) {
        const hit = hits[i];
        if (hit.distance < closest_hit.distance) {
            closest_hit = hit;
        }
    }
    return closest_hit;
}
export function filter_hits_to_one_per_interactable_in_place(hits) {
    // Find only the closest hit per object
    const interactable_to_hit = new Map();
    for (let i = 0; i < hits.length; i++) {
        const hit = hits[i];
        const old_hit = interactable_to_hit.get(hit.object);
        if (old_hit !== undefined && hit.distance < old_hit.distance) {
            interactable_to_hit.set(hit.object, hit);
        }
        else {
            interactable_to_hit.set(hit.object, hit);
        }
    }
    hits.length = 0;
    for (const hit of interactable_to_hit.values()) {
        hits.push(hit);
    }
}
export function update_babylon_ray(ray, origin_or_ray, direction, length) {
    if ('origin' in origin_or_ray) { // TODO: Make IRay.is() type guard when we move to static classes
        ray.origin.fromIVector3(origin_or_ray.origin);
        ray.direction.fromIVector3(origin_or_ray.direction);
        ray.direction.normalize();
        ray.length = origin_or_ray.length;
    }
    else {
        ray.origin.fromIVector3(origin_or_ray);
        if (direction && 'x' in direction) {
            ray.direction.fromIVector3(direction);
        }
        ray.direction.normalize();
        ray.length = length !== null && length !== void 0 ? length : Number.MAX_VALUE;
    }
}
// Should perhaps be called is_backface_triangle or similar?
export function filter_backface_triangles(p0, p1, p2, ray) {
    const p0p1 = p0.subtract(p1);
    const p2p1 = p2.subtract(p1);
    const normal = BABYLON_Vector3.Cross(p0p1, p2p1);
    return (BABYLON_Vector3.Dot(ray.direction, normal) > 0);
}
const BVH_MAX_DEPTH = 20;
const BVH_MAX_TRIANGLES = 20;
const BVH_MIN_BB_SIZE = 0.0001;
export function build_triangles_bvh(positions, indices) {
    assert(indices.length % 3 === 0, 'Error: bad data in index buffer, not devisable by 3');
    assert(positions.length % 3 === 0, 'Error: bad data in positions buffer, not devisable by 3');
    const triangles = [];
    const num_triangles = indices.length / 3;
    triangles.length = num_triangles;
    for (let i = 0; i < num_triangles; i++) {
        const index0 = indices[i * 3 + 0];
        const index1 = indices[i * 3 + 1];
        const index2 = indices[i * 3 + 2];
        const cx = (positions[index0 * 3 + 0] + positions[index1 * 3 + 0] + positions[index2 * 3 + 0]) / 3;
        const cy = (positions[index0 * 3 + 1] + positions[index1 * 3 + 1] + positions[index2 * 3 + 1]) / 3;
        const cz = (positions[index0 * 3 + 2] + positions[index1 * 3 + 2] + positions[index2 * 3 + 2]) / 3;
        triangles[i] = {
            centroid: { x: cx, y: cy, z: cz },
            triangle_index: i
        };
    }
    return triangles;
}
function find_split_axis(bound) {
    const sizex = bound.max.x - bound.min.x;
    const sizey = bound.max.y - bound.min.y;
    const sizez = bound.max.z - bound.min.z;
    if (sizex >= sizey && sizex >= sizez) {
        return 'x';
    }
    else if (sizey >= sizex && sizey >= sizez) {
        return 'y';
    }
    else {
        return 'z';
    }
}
export function build_tree_bvh(triangles, positions, indices, depth = 0) {
    const left_triangles = [];
    const right_triangles = [];
    let left_node = null;
    let right_node = null;
    let node_triangles = [];
    const bounding_box = IBoundingBox.Invalid();
    for (const triangle of triangles) {
        IBoundingBox.encapsulate_in_place(bounding_box, triangle.centroid);
    }
    const split_axis = find_split_axis(bounding_box);
    const axis_length = bounding_box.max[split_axis] - bounding_box.min[split_axis];
    const split_value = lerp(bounding_box.min[split_axis], bounding_box.max[split_axis], 0.5);
    if (triangles.length <= BVH_MAX_TRIANGLES ||
        depth >= BVH_MAX_DEPTH ||
        axis_length < BVH_MIN_BB_SIZE) {
        node_triangles = triangles.map(t => { return { triangle_index: t.triangle_index }; });
        // Find actual bound size
        for (const triangle of node_triangles) {
            const index0 = indices[triangle.triangle_index * 3 + 0];
            const index1 = indices[triangle.triangle_index * 3 + 1];
            const index2 = indices[triangle.triangle_index * 3 + 2];
            const p0 = {
                x: positions[index0 * 3 + 0],
                y: positions[index0 * 3 + 1],
                z: positions[index0 * 3 + 2]
            };
            const p1 = {
                x: positions[index1 * 3 + 0],
                y: positions[index1 * 3 + 1],
                z: positions[index1 * 3 + 2]
            };
            const p2 = {
                x: positions[index2 * 3 + 0],
                y: positions[index2 * 3 + 1],
                z: positions[index2 * 3 + 2]
            };
            IBoundingBox.encapsulate_in_place(bounding_box, p0, p1, p2);
        }
    }
    else {
        for (const triangle of triangles) {
            if (triangle.centroid[split_axis] < split_value) {
                left_triangles.push(triangle);
            }
            else {
                right_triangles.push(triangle);
            }
        }
        left_node = build_tree_bvh(left_triangles, positions, indices, depth + 1);
        right_node = build_tree_bvh(right_triangles, positions, indices, depth + 1);
        IBoundingBox.encapsulate_in_place(bounding_box, left_node.bounding_box.min, left_node.bounding_box.max, right_node.bounding_box.min, right_node.bounding_box.max);
    }
    return {
        triangles: node_triangles,
        bounding_box: bounding_box,
        left: left_node,
        right: right_node
    };
}
const normal = new IVector3();
const edge10 = new IVector3();
const edge20 = new IVector3();
const p0 = new IVector3();
const p1 = new IVector3();
const p2 = new IVector3();
const h = new IVector3();
const s = new IVector3();
const q = new IVector3();
const intersection_point = new IVector3();
// Based on https://en.wikipedia.org/wiki/M%C3%B6ller%E2%80%93Trumbore_intersection_algorithm
export function intersect_ray_triangle(ray, triangle, positions, indices, hit, filter_backface) {
    const epsilon = 0.0000001;
    const index0 = indices[triangle.triangle_index * 3 + 0];
    const index1 = indices[triangle.triangle_index * 3 + 1];
    const index2 = indices[triangle.triangle_index * 3 + 2];
    IVector3.set(p0, positions[index0 * 3 + 0], positions[index0 * 3 + 1], positions[index0 * 3 + 2]);
    IVector3.set(p1, positions[index1 * 3 + 0], positions[index1 * 3 + 1], positions[index1 * 3 + 2]);
    IVector3.set(p2, positions[index2 * 3 + 0], positions[index2 * 3 + 1], positions[index2 * 3 + 2]);
    IVector3.subtract_to_ref(p1, p0, edge10);
    IVector3.subtract_to_ref(p2, p0, edge20);
    IVector3.cross_to_ref(ray.direction, edge20, h);
    const a = IVector3.dot(edge10, h);
    if (a > -epsilon && a < epsilon)
        return false;
    const f = 1.0 / a;
    IVector3.subtract_to_ref(ray.origin, p0, s);
    const u = f * (IVector3.dot(s, h));
    if (u < 0.0 || u > 1.0)
        return false;
    IVector3.cross_to_ref(s, edge10, q);
    const v = f * (IVector3.dot(ray.direction, q));
    if (v < 0.0 || (u + v) > 1.0)
        return false;
    const t = f * (IVector3.dot(edge20, q));
    if (t > epsilon) {
        IVector3.copy_to_ref(ray.origin, intersection_point);
        IVector3.add_scaled_in_place(intersection_point, t, ray.direction);
        const distance = IVector3.distance(intersection_point, ray.origin);
        if (filter_backface) {
            IVector3.cross_to_ref(edge20, edge10, normal);
            if (IVector3.dot(ray.direction, normal) < 0) {
                return false;
            }
        }
        if (hit.distance > distance) {
            hit.distance = distance;
            hit.world_position = IVector3.copy(intersection_point);
            return true;
        }
    }
    return false;
}
const hit_distance_cache = new IVector2();
export function raycast_bvh(ray, node, positions, indices, hit, filter_backface, triangle_hit) {
    const intersects_bounds = IRay.intersects_bounding_box(ray, node.bounding_box, hit_distance_cache);
    if (!intersects_bounds)
        return triangle_hit;
    // Both hits are behind ray origin
    if (hit_distance_cache.x < 0 && hit_distance_cache.y < 0)
        return triangle_hit;
    // Bounds for this node is further away than our previous hit
    if (triangle_hit && hit_distance_cache.x > hit.distance)
        return triangle_hit;
    for (const triangle of node.triangles) {
        triangle_hit = intersect_ray_triangle(ray, triangle, positions, indices, hit, filter_backface) || triangle_hit;
    }
    if (node.left) {
        triangle_hit = raycast_bvh(ray, node.left, positions, indices, hit, filter_backface, triangle_hit) || triangle_hit;
    }
    if (node.right) {
        triangle_hit = raycast_bvh(ray, node.right, positions, indices, hit, filter_backface, triangle_hit) || triangle_hit;
    }
    return triangle_hit;
}
export function build_bvh_from_mesh(mesh) {
    const positions = mesh.getVerticesData(BABYLON_VertexBuffer.PositionKind);
    const indices = mesh.getIndices();
    if (positions && positions.length > 0 && indices && indices.length > 0) {
        const triangles = build_triangles_bvh(positions, indices);
        return build_tree_bvh(triangles, positions, indices);
    }
    else {
        return {
            left: null,
            right: null,
            triangles: [],
            bounding_box: {
                min: { x: 0, y: 0, z: 0 },
                max: { x: 0, y: 0, z: 0 }
            }
        };
    }
}
//# sourceMappingURL=Raycast.js.map